import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import {
  validateImage,
  validateImageDimensions,
  resizeImage,
  NEWS_IMAGE_CONSTRAINTS,
} from '../../utils/imageUtils';

interface NewsImageUploadProps {
  value?: File | string | null;
  onChange: (file: File | null) => void;
  error?: string;
  disabled?: boolean;
  articleId?: string;
}

const NewsImageUpload: React.FC<NewsImageUploadProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  articleId,
}) => {
  const { t } = useTranslation();
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    let url: string | null = null;
    if (value instanceof File) {
      url = URL.createObjectURL(value);
      setPreviewUrl(url);
    } else if (typeof value === 'string' && value) {
      setPreviewUrl(value);
    } else {
      setPreviewUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [value]);

  const handleImageError = () => {
    setPreviewUrl(null);
    onChange(null);
  };

  const processFile = async (file: File) => {
    setIsValidating(true);
    setValidationError(null);

    try {
      const basicValidation = validateImage(file, NEWS_IMAGE_CONSTRAINTS);
      if (!basicValidation.isValid) {
        setValidationError(basicValidation.error!);
        return;
      }

      const dimensionValidation = await validateImageDimensions(file, NEWS_IMAGE_CONSTRAINTS);
      if (!dimensionValidation.isValid) {
        setValidationError(dimensionValidation.error!);
        return;
      }

      const processedFile = await resizeImage(
        file,
        NEWS_IMAGE_CONSTRAINTS.maxWidth,
        NEWS_IMAGE_CONSTRAINTS.maxHeight
      );
      onChange(processedFile);
    } catch {
      setValidationError(t('imageUpload.failedToProcess'));
    } finally {
      setIsValidating(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleRemoveImage = () => {
    if (value instanceof File || !articleId || typeof value !== 'string') {
      onChange(null);
      setValidationError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!articleId) return;
    setIsDeleting(true);
    setValidationError(null);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/news/${articleId}/image`);
      onChange(null);
      setConfirmOpen(false);
    } catch {
      setValidationError(t('imageUpload.failedToDelete'));
    } finally {
      setIsDeleting(false);
    }
  };

  const getDisplayError = () => validationError || error;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
        {t('newsForm.featuredImage')}
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {previewUrl ? (
        <Paper
          sx={{
            p: 2,
            width: 250,
            height: 250,
            margin: '0 auto',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={previewUrl}
            alt="Preview"
            onError={handleImageError}
            sx={{
              width: 250,
              height: 250,
              objectFit: 'contain',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
          <IconButton
            onClick={handleRemoveImage}
            disabled={disabled}
            color="error"
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <DeleteIcon />
          </IconButton>
        </Paper>
      ) : (
        <Paper
          sx={{
            p: 3,
            width: 250,
            height: 250,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : getDisplayError() ? 'error.main' : 'divider',
            borderRadius: 2,
            textAlign: 'center',
            cursor: disabled ? 'default' : 'pointer',
            backgroundColor: isDragOver ? 'action.hover' : 'transparent',
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setIsDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragOver(false);
          }}
          onDrop={async (e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (disabled) return;
            const imageFile = Array.from(e.dataTransfer.files).find((file) =>
              file.type.startsWith('image/')
            );
            if (imageFile) {
              await processFile(imageFile);
            } else {
              setValidationError(t('imageUpload.dropValidImage'));
            }
          }}
        >
          {isValidating ? (
            <CircularProgress size={40} />
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('imageUpload.clickToSelectOrDrag')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {t('imageUpload.newsImageRequirements')}
              </Typography>
            </>
          )}
        </Paper>
      )}

      {getDisplayError() && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {getDisplayError()}
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('imageUpload.confirmDeletion')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('imageUpload.confirmDeleteMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : t('imageUpload.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsImageUpload;
